/** @format */

import React, { useMemo } from "react"; // Added useMemo
import { useSelector } from "react-redux";
import { Trash2, Edit, Copy, Plus, Minus } from "lucide-react";
import BlindRender from "../components/BlindRender"; // Assuming path is correct

// --- Helper Functions (Keep or move to utils) ---
export const getHemRenderPath = (productInfo) => {
  /* ... same logic ... */
  if (!productInfo) return "null/standard";
  let type = "";
  const lcBottomBar = productInfo.Bottom_Bar?.toLowerCase();
  const wrapType = productInfo.Hem_Wrap_Type;
  if (wrapType === "Yes" && lcBottomBar?.includes("accubar")) type = "wab";
  else if (lcBottomBar?.includes("accubar")) type = "ab";
  else if (lcBottomBar?.includes("plain hem")) type = "ph";
  else if (lcBottomBar?.includes("illusion")) type = "ill";
  else if (lcBottomBar?.includes("interlude")) type = "int";
  else type = "ph";
  const color =
    lcBottomBar === "plain hem"
      ? "standard"
      : (productInfo.Bottom_Bar_Color || "white").toLowerCase();
  return `${type}/${color}`;
};
export const valanceRender = (name, color) => {
  /* ... same logic ... */
  if (!name) return "render/valance/or/white.png";
  let code;
  const lcName = name.toLowerCase();
  switch (lcName) {
    case "compact curved cassette":
    case "compact curved casette":
    case "sb compact curved cassette":
      code = "ccc";
      break;
    case "open roll":
      code = "or";
      break;
    case '3" fascia':
    case 'sb 3" wrapped fascia':
      code = "3f";
      break;
    case '4" fascia':
    case 'sb 4" wrapped fascia':
      code = "4f";
      break;
    case "curved cassette s":
      code = "d8";
      break;
    case "curved cassette m":
    case "curved cassette":
    case "sb wrapped curved cassette":
      code = "d12";
      break;
    case "curved cassette l":
      code = "d16";
      break;
    case "pvc valance":
      code = "pvc";
      break;
    case "square cassette":
      code = "sc";
      break;
    default:
      code = "or";
  }
  const lcColor = (color || "white").toLowerCase();
  return `render/valance/${code}/${lcColor}.png`;
};
const getHemTypeDisplayName = (bottomBar) => {
  /* ... same logic ... */
  if (!bottomBar) return "-";
  const nameMap = {
    "accubar 2.1": "Aluminum Hem",
    "interlude 2.0": "Aluminum Bar",
    illusion: "Aluminum Bar",
    "plain hem": "Plain Hem",
  };
  return nameMap[bottomBar.toLowerCase()] || bottomBar;
};
export const getDriveTypeDisplayName = (type) => {
  /* ... same logic ... */
  if (!type) return "-";
  const nameMap = {
    "zero gravity": "Glide Chainless Manual",
    chain: "Chain Manual",
    motor: "Automated (Motorized)",
  };
  return nameMap[type.toLowerCase()] || type;
};
const getValanceTypeDisplayName = (valanceName) => {
  /* ... same logic ... */
  if (!valanceName) return "-";
  const nameMap = {
    'sb 3" fascia': '3" Fascia',
    "sb compact curved cassette": "Compact Curved Cassette",
    'sb 4" fascia': '4" Fascia',
    "sb curved cassette": "Curved Cassette",
    "open roll": "Open Roll",
    "square cassette": "Square Cassette",
  };
  const lowerCaseValance = valanceName
    .toLowerCase()
    .replace(" wrapped", "")
    .replace("wrapped ", "");
  return nameMap[lowerCaseValance] || valanceName;
};

// --- Main CartItem Component ---
export const CartItem = ({
  item,
  index,
  country, // Receive country as prop
  onQuantityChange,
  onRemove,
  onEdit,
  onDuplicate,
}) => {
  // Memoize derived values to prevent recalculations if item object reference doesn't change
  const productInfo = useMemo(
    () => item?.productInfo || {},
    [item?.productInfo],
  );
  const optionInfo = useMemo(() => item?.optionInfo || {}, [item?.optionInfo]);

  const hasDiscount = useMemo(
    () =>
      (parseFloat(productInfo?.Discount) || 0) > 0 ||
      (parseFloat(productInfo?.Global_Discount) || 0) > 0,
    [productInfo?.Discount, productInfo?.Global_Discount],
  );
  const isCellular = productInfo?.Type === "Simply Cellular";
  const isVerticalSheer = productInfo?.Type === "Vertical Sheer";

  // Prepare render paths
  // Calculate price display strings
  const retailPrice = parseFloat(optionInfo?.RETAIL_PRICE) || 0;
  const ourPrice = parseFloat(optionInfo?.OUR_PRICE) || 0;
  const currencyCode = country === "USA" ? "USD" : "CAD";

  const displayRetailPrice = `$${retailPrice.toFixed(2)} ${currencyCode}`;
  const displayOurPrice = `$${ourPrice.toFixed(2)} ${currencyCode}`;

  // Memoize render paths
  const fabricRenderStr = useMemo(
    () =>
      `${productInfo?.Fabric?.toLowerCase() || ""} ${
        productInfo?.SG_Color?.toLowerCase() || ""
      }`.trim(),
    [productInfo?.Fabric, productInfo?.SG_Color],
  );
  const hemRenderPath = useMemo(
    () => getHemRenderPath(productInfo),
    [productInfo],
  );
  const valanceRenderPath = useMemo(
    () =>
      valanceRender(
        productInfo?.Valance,
        productInfo?.Valance_Color,
      ).toLowerCase(),
    [productInfo?.Valance, productInfo?.Valance_Color],
  );

  // Simplify details for display
  const sizeDisplay = `${parseFloat(optionInfo?.widthWhole).toFixed(2) || "?"}" W x ${parseFloat(optionInfo?.lengthWhole).toFixed(2) || "?"}" H`;
  const mountDisplay =
    optionInfo?.measure === "Tight" ? "Inside Mount" : "Outside Mount";
  const fabricDisplay = `${
    productInfo?.New_Fabric ||
    productInfo?.OCS_Name ||
    productInfo?.Fabric ||
    "?"
  } - ${
    productInfo?.New_Colour ||
    productInfo?.OCS_Colour ||
    optionInfo?.selectedColor ||
    "?"
  }`;
  const controlDisplay = `${getDriveTypeDisplayName(productInfo?.Operation)} (${
    productInfo?.position || "N/A"
  })`;
  const valanceDisplay = `${getValanceTypeDisplayName(productInfo?.Valance)} (${
    productInfo?.Valance_Color || "N/A"
  })`;
  const bottomBarDisplay = `${getHemTypeDisplayName(
    productInfo?.Bottom_Bar,
  )} (${productInfo?.Bottom_Bar_Color || "N/A"})`;
  const rollTypeDisplay =
    productInfo?.Roll_Type === "Reverse" ? "Reverse Roll" : "Regular Roll";

  const itemName =
    productInfo?.New_Program ||
    productInfo?.Program ||
    `${productInfo?.Type || ""} Shade`;

  return (
    <div className="bg-white shadow-sm border border-gray-200 p-4">
      {/* Use Grid for better layout control */}
      <div className="grid grid-cols-1 sm:grid-cols-[100px_1fr_auto] md:grid-cols-[150px_1fr_auto] gap-4 items-start">
        {/* Column 1: Product Image */}
        <div className="w-full aspect-[3/4] overflow-hidden ">
          {/* Add placeholder/loading state? */}
          <BlindRender
            renderFabric={fabricRenderStr}
            renderHem={hemRenderPath}
            renderValance={valanceRenderPath}
            isVerticalSheer={isVerticalSheer}
          />
        </div>

        {/* Column 2: Product Details & Customizations */}
        <div className="flex-1 space-y-2 min-w-0 max-md:mx-auto">
          {" "}
          {/* min-w-0 prevents text overflow issues */}
          <h3 className="font-semibold text-base leading-snug text-gray-800 max-md:text-xl">
            {/* Display simplified name */}
            {productInfo?.Type || "Custom Shade"}
            {productInfo?.OCS_Name && ` - ${productInfo.OCS_Name}`}
          </h3>
          {/* Key Customizations - using definition list for semantics */}
          <dl className="text-xs text-gray-600 space-y-1 max-md:text-sm">
            <div className="flex">
              <dt className="w-20 font-medium flex-shrink-0">Size:</dt>
              <dd className="truncate" title={sizeDisplay}>
                {sizeDisplay}
              </dd>
            </div>
            <div className="flex">
              <dt className="w-20 font-medium flex-shrink-0">Mount:</dt>
              <dd>{mountDisplay}</dd>
            </div>
            {isVerticalSheer && optionInfo?.stackType && (
              <div className="flex">
                <dt className="w-20 font-medium flex-shrink-0">Stack:</dt>
                <dd>{optionInfo.stackType}</dd>
              </div>
            )}
            <div className="flex">
              <dt className="w-20 font-medium flex-shrink-0">Fabric:</dt>
              <dd className="truncate" title={fabricDisplay}>
                {fabricDisplay}
              </dd>
            </div>
            {/* Conditional Details for non-Cellular */}
            {!isCellular && !isVerticalSheer && (
              <>
                <div className="flex">
                  <dt className="w-20 font-medium flex-shrink-0">Control:</dt>
                  <dd>{controlDisplay}</dd>
                </div>
                {productInfo?.Roll_Type && (
                  <div className="flex">
                    <dt className="w-20 font-medium flex-shrink-0">
                      Roll Type:
                    </dt>
                    <dd>{rollTypeDisplay}</dd>
                  </div>
                )}
                {productInfo?.Valance !== "Open Roll" && (
                  <div className="flex">
                    <dt className="w-20 font-medium flex-shrink-0">Valance:</dt>
                    <dd>{valanceDisplay}</dd>
                  </div>
                )}
                <div className="flex">
                  <dt className="w-20 font-medium flex-shrink-0">
                    Bottom Bar:
                  </dt>
                  <dd>{bottomBarDisplay}</dd>
                </div>
              </>
            )}
            {/* Add other relevant details */}
          </dl>
          {/* Actions (Moved here for better mobile layout) */}
          <div className="flex items-center gap-6 pt-2 sm:hidden max-md:justify-center">
            {" "}
            {/* Show only on smaller screens */}
            <button
              onClick={() => onEdit(index)}
              className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
              title="Edit Customization"
            >
              {" "}
              <Edit size={25} />{" "}
            </button>
            <button
              onClick={() => onDuplicate(index)}
              className="p-1.5 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-md transition-colors"
              title="Duplicate Item"
            >
              {" "}
              <Copy size={25} />{" "}
            </button>
            <button
              onClick={() => onRemove(index, itemName)}
              className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
              title="Remove Item"
            >
              {" "}
              <Trash2 size={25} />{" "}
            </button>
          </div>
        </div>

        {/* Column 3: Price & Quantity/Actions */}
        <div className="flex flex-col items-end justify-between gap-4 w-full sm:w-auto">
          {/* Price */}
          <div className="text-right space-y-0.5 w-full sm:w-auto max-md:text-center ">
            {hasDiscount ? (
              <>
                <div className="line-through text-gray-500 text-sm max-md:text-lg">
                  {displayRetailPrice}
                </div>
                <div className="text-primary font-semibold text-base max-md:text-lg">
                  {displayOurPrice}
                </div>
              </>
            ) : (
              <div className="font-semibold text-base text-gray-800 max-md:text-lg">
                {displayOurPrice}
              </div>
            )}
            {/* <div className="text-xs text-gray-500">per item</div> */}
          </div>

          {/* Quantity */}
          <div className="flex items-center gap-1 border border-gray-300 rounded-md p-1 w-full justify-between sm:w-auto sm:justify-start">
            <button
              onClick={() =>
                onQuantityChange(index, (optionInfo?.quantity || 1) - 1)
              }
              disabled={(optionInfo?.quantity || 1) <= 1}
              className="p-1 text-gray-500 hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Decrease quantity"
            >
              <Minus size={16} />
            </button>
            <input
              type="number"
              min="1"
              aria-label="Quantity"
              value={optionInfo?.quantity || 1}
              onChange={(e) =>
                onQuantityChange(index, parseInt(e.target.value) || 1)
              } // Ensure positive integer
              className="w-10 text-center font-medium border-none focus:ring-0 p-0 bg-transparent" // Minimal input style
            />
            <button
              onClick={() =>
                onQuantityChange(index, (optionInfo?.quantity || 1) + 1)
              }
              className="p-1 text-gray-500 hover:text-primary"
              aria-label="Increase quantity"
            >
              <Plus size={16} />
            </button>
          </div>

          {/* Action Buttons (Desktop) */}
          <div className="hidden sm:flex items-center justify-end gap-1 mt-auto">
            <button
              onClick={() => onEdit(index)}
              className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
              title="Edit Customization"
            >
              {" "}
              <Edit size={16} />{" "}
            </button>
            <button
              onClick={() => onDuplicate(index)}
              className="p-1.5 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-md transition-colors"
              title="Duplicate Item"
            >
              {" "}
              <Copy size={16} />{" "}
            </button>
            <button
              onClick={() => onRemove(index, itemName)}
              className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
              title="Remove Item"
            >
              {" "}
              <Trash2 size={16} />{" "}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
